<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import ProductModal from '../components/ProductModal.svelte';
  import ProductVariantModal from '../components/ProductVariantModal.svelte';
  import CustomerPricesModal from '../components/CustomerPricesModal.svelte';
  import { productsApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import { sanitizeRichText } from '../lib/sanitizeHtml.js';
  import { Box, Search, Plus, ChevronDown, ChevronRight, Edit2, Trash2, Tag, Server, RefreshCw, Clock, Users, AlertTriangle, X, Info } from 'lucide-svelte';

  let products = [];
  let filteredProducts = [];
  let loading = false;
  let error = '';
  let searchQuery = '';
  let categoryFilter = '';
  let expandedProducts = new Set();

  // Product modal state
  let showProductModal = false;
  let productToEdit = null;

  // Variant modal state
  let showVariantModal = false;
  let variantToEdit = null;
  let variantProductId = null;

  // Delete confirmation
  let deletingProduct = null;
  let deletingVariant = null;

  // Delete modal state
  let showDeleteModal = false;
  let deleteTarget = null; // { type: 'product' | 'variant', item: object, productId?: number }

  // Customer prices modal state
  let showCustomerPricesModal = false;
  let customerPricesVariant = null;

  // Description modal state
  let showDescriptionModal = false;
  let descriptionProduct = null;

  const CATEGORIES = [
    { value: '', label: 'All categories' },
    { value: 'service', label: 'Services' },
    { value: 'subscription', label: 'Subscriptions' },
    { value: 'hosting', label: 'Hosting' }
  ];

  const BILLING_CYCLES = {
    one_time: 'One-time',
    monthly: 'Monthly',
    bimonthly: 'Bimonthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly'
  };

  const CATEGORY_ICONS = {
    service: Tag,
    subscription: RefreshCw,
    hosting: Server
  };

  const CATEGORY_COLORS = {
    service: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200',
    subscription: 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200',
    hosting: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200'
  };

  onMount(() => {
    loadProducts();
  });

  async function loadProducts() {
    try {
      loading = true;
      error = '';

      const loadedProducts = await productsApi.getAll();
      products = loadedProducts;
      console.log('📦 Loaded products:', products);

    } catch (err) {
      error = err.message;
      console.error('❌ Load products error:', err);
    } finally {
      loading = false;
    }
  }

  // Format price for display
  function formatPrice(price) {
    if (price === null || price === undefined) return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '-';
    return `€${numPrice.toFixed(2)}`;
  }

  // Toggle product expansion
  function toggleProduct(productId) {
    if (expandedProducts.has(productId)) {
      expandedProducts.delete(productId);
    } else {
      expandedProducts.add(productId);
    }
    expandedProducts = new Set(expandedProducts);
  }

  // Open product modal for creating new product
  function openProductModal(product = null) {
    productToEdit = product;
    showProductModal = true;
  }

  // Close product modal
  function closeProductModal() {
    showProductModal = false;
    productToEdit = null;
  }

  // Open variant modal
  function openVariantModal(productId, variant = null) {
    variantProductId = productId;
    variantToEdit = variant;
    showVariantModal = true;
  }

  // Close variant modal
  function closeVariantModal() {
    showVariantModal = false;
    variantToEdit = null;
    variantProductId = null;
  }

  // Handle product saved
  async function handleProductSaved() {
    await loadProducts();
    closeProductModal();
  }

  // Handle variant saved
  async function handleVariantSaved() {
    await loadProducts();
    closeVariantModal();
  }

  // Open customer prices modal
  function openCustomerPricesModal(variant) {
    customerPricesVariant = variant;
    showCustomerPricesModal = true;
  }

  // Close customer prices modal
  function closeCustomerPricesModal() {
    showCustomerPricesModal = false;
    customerPricesVariant = null;
  }

  // Open description modal
  function openDescriptionModal(product) {
    descriptionProduct = product;
    showDescriptionModal = true;
  }

  // Close description modal
  function closeDescriptionModal() {
    showDescriptionModal = false;
    descriptionProduct = null;
  }

  // Open delete confirmation modal
  function confirmDeleteProduct(product) {
    deleteTarget = { type: 'product', item: product };
    showDeleteModal = true;
  }

  function confirmDeleteVariant(productId, variant) {
    deleteTarget = { type: 'variant', item: variant, productId };
    showDeleteModal = true;
  }

  function closeDeleteModal() {
    showDeleteModal = false;
    deleteTarget = null;
  }

  // Handle confirmed deletion
  async function handleConfirmDelete() {
    if (!deleteTarget) return;

    try {
      if (deleteTarget.type === 'product') {
        deletingProduct = deleteTarget.item.id;
        await productsApi.delete(deleteTarget.item.id);
        console.log('🗑️ Product deleted:', deleteTarget.item.code);
      } else if (deleteTarget.type === 'variant') {
        deletingVariant = deleteTarget.item.id;
        await productsApi.deleteVariant(deleteTarget.productId, deleteTarget.item.id);
        console.log('🗑️ Variant deleted:', deleteTarget.item.code);
      }
      await loadProducts();
      closeDeleteModal();
    } catch (err) {
      error = err.message;
      console.error('❌ Delete error:', err);
    } finally {
      deletingProduct = null;
      deletingVariant = null;
    }
  }

  // Get category label
  function getCategoryLabel(category) {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.label : category;
  }

  // Filter products
  $: {
    let result = products;

    // Apply category filter (filter products that have at least one variant with the selected category)
    if (categoryFilter) {
      result = result.filter(product =>
        product.variants?.some(v => v.category === categoryFilter)
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(product =>
        (product.code?.toLowerCase().includes(query)) ||
        (product.name?.toLowerCase().includes(query)) ||
        (product.description?.toLowerCase().includes(query)) ||
        (product.variants?.some(v =>
          v.code?.toLowerCase().includes(query) ||
          v.name?.toLowerCase().includes(query)
        ))
      );
    }

    filteredProducts = result;
  }
</script>

<Layout>
  <div class="max-w-7xl mx-auto">
    <!-- Header -->
    <div class="page-header">
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div class="space-y-3">
          <div class="flex items-center space-x-3">
            <div class="w-12 h-12 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
              <Box size={24} class="text-white" />
            </div>
            <div>
              <h1 class="text-3xl font-bold text-gray-900 dark:text-white">{$_('products.title')}</h1>
              <p class="text-gray-600 dark:text-gray-400 font-medium">{$_('products.subtitle')}</p>
            </div>
          </div>
          <div class="flex items-center space-x-4 text-sm">
            <div class="flex items-center space-x-2 bg-primary-50 dark:bg-primary-900/30 px-3 py-1 rounded-full">
              <div class="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
              <span class="text-primary-700 dark:text-primary-300 font-semibold">{$_('products.totalCount', { values: { count: filteredProducts.length } })}</span>
            </div>
          </div>
        </div>
        <div class="flex space-x-3">
          <button
            onclick={() => openProductModal()}
            class="btn btn-primary shadow-primary transform hover:scale-105"
          >
            <Plus size={18} class="mr-2" />
            <span class="font-semibold">{$_('products.add')}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Search and Filters -->
    <div class="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center">
      <div class="relative flex-1 md:flex-none">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={20} class="text-gray-400 dark:text-gray-500" />
        </div>
        <input
          type="text"
          bind:value={searchQuery}
          placeholder={$_('products.searchPlaceholder')}
          class="input pl-10 w-full md:w-96"
        />
      </div>

      <!-- Category Filter -->
      <select
        bind:value={categoryFilter}
        class="input w-full md:w-48"
      >
        {#each CATEGORIES as cat}
          <option value={cat.value}>{cat.label}</option>
        {/each}
      </select>
    </div>

    <!-- Error Message -->
    {#if error}
      <div class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-4 mb-6">
        <div class="text-sm text-red-800 dark:text-red-200">{error}</div>
      </div>
    {/if}

    <!-- Products List -->
    <div class="space-y-4">
      {#if loading}
        <div class="card-elevated p-12 text-center">
          <div class="loading-spinner h-12 w-12 mx-auto mb-4"></div>
          <div class="space-y-2">
            <p class="text-lg font-medium text-gray-700 dark:text-gray-300">{$_('common.loading')}</p>
            <p class="text-sm text-gray-500 dark:text-gray-400">{$_('products.loadingMessage')}</p>
          </div>
        </div>
      {:else if filteredProducts.length === 0}
        <div class="card-elevated p-12 text-center">
          <div class="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
            <Box size={32} class="text-gray-400 dark:text-gray-500" />
          </div>
          <div class="space-y-2">
            <h3 class="text-lg font-medium text-gray-900 dark:text-white">
              {searchQuery || categoryFilter ? $_('products.noResults') : $_('products.noProducts')}
            </h3>
            <p class="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
              {searchQuery || categoryFilter ? $_('products.noResultsHint') : $_('products.noProductsHint')}
            </p>
          </div>
        </div>
      {:else}
        {#each filteredProducts as product}
          <div class="card-elevated overflow-hidden">
            <!-- Product Header -->
            <div
              class="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              onclick={() => toggleProduct(product.id)}
              role="button"
              tabindex="0"
              onkeydown={(e) => e.key === 'Enter' && toggleProduct(product.id)}
            >
              <div class="flex items-center space-x-4">
                <button class="text-gray-500 dark:text-gray-400">
                  {#if expandedProducts.has(product.id)}
                    <ChevronDown size={20} />
                  {:else}
                    <ChevronRight size={20} />
                  {/if}
                </button>
                <div>
                  <div class="flex items-center space-x-3">
                    <span class="text-sm font-mono text-gray-500 dark:text-gray-400">{product.code}</span>
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{product.name}</h3>
                    {#if !product.isActive}
                      <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                        Inactive
                      </span>
                    {/if}
                    {#if product.description}
                      <button
                        onclick={(e) => { e.stopPropagation(); openDescriptionModal(product); }}
                        class="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                        title="View description"
                      >
                        <Info size={16} />
                      </button>
                    {/if}
                  </div>
                </div>
              </div>
              <div class="flex items-center space-x-3">
                <span class="text-sm text-gray-500 dark:text-gray-400">
                  {product.variantsCount || 0} variants
                </span>
                <div class="flex items-center space-x-2" onclick={(e) => e.stopPropagation()}>
                  <button
                    onclick={() => openProductModal(product)}
                    class="p-2 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    title="Edit product"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onclick={() => confirmDeleteProduct(product)}
                    disabled={deletingProduct === product.id}
                    class="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
                    title="Delete product"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>

            <!-- Variants Table (Expanded) -->
            {#if expandedProducts.has(product.id)}
              <div class="border-t border-gray-200 dark:border-gray-700">
                {#if product.variants && product.variants.length > 0}
                  <div class="overflow-x-auto">
                    <table class="w-full">
                      <thead class="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Code</th>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Name</th>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Category</th>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Price</th>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Unit</th>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Cycle</th>
                          <th class="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
                          <th class="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {#each product.variants as variant}
                          <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                            <td class="px-6 py-3 whitespace-nowrap">
                              <span class="text-sm font-mono text-gray-600 dark:text-gray-400">{variant.code}</span>
                            </td>
                            <td class="px-6 py-3">
                              <span class="text-sm font-medium text-gray-900 dark:text-white">{variant.name}</span>
                              {#if variant.description}
                                <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 prose prose-xs dark:prose-invert max-w-none">{@html sanitizeRichText(variant.description)}</div>
                              {/if}
                            </td>
                            <td class="px-6 py-3 whitespace-nowrap">
                              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium {CATEGORY_COLORS[variant.category] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}">
                                <svelte:component this={CATEGORY_ICONS[variant.category] || Tag} size={12} class="mr-1" />
                                {getCategoryLabel(variant.category)}
                              </span>
                            </td>
                            <td class="px-6 py-3 whitespace-nowrap">
                              <span class="text-sm font-semibold text-gray-900 dark:text-white">{formatPrice(variant.price)}</span>
                            </td>
                            <td class="px-6 py-3 whitespace-nowrap">
                              <span class="text-sm text-gray-600 dark:text-gray-400">{variant.unit || '-'}</span>
                            </td>
                            <td class="px-6 py-3 whitespace-nowrap">
                              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                <Clock size={12} class="mr-1" />
                                {BILLING_CYCLES[variant.billingCycle] || variant.billingCycle}
                              </span>
                            </td>
                            <td class="px-6 py-3 whitespace-nowrap">
                              {#if variant.isActive}
                                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                                  Active
                                </span>
                              {:else}
                                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                  Inactive
                                </span>
                              {/if}
                            </td>
                            <td class="px-6 py-3 whitespace-nowrap text-right">
                              <div class="flex items-center justify-end space-x-2">
                                <button
                                  onclick={() => openCustomerPricesModal(variant)}
                                  class="p-1.5 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                                  title="Customer prices"
                                >
                                  <Users size={14} />
                                </button>
                                <button
                                  onclick={() => openVariantModal(product.id, variant)}
                                  class="p-1.5 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                                  title="Edit variant"
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  onclick={() => confirmDeleteVariant(product.id, variant)}
                                  disabled={deletingVariant === variant.id}
                                  class="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors disabled:opacity-50"
                                  title="Delete variant"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        {/each}
                      </tbody>
                    </table>
                  </div>
                {:else}
                  <div class="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    <p class="text-sm">No variants for this product</p>
                  </div>
                {/if}

                <!-- Add Variant Button -->
                <div class="px-6 py-3 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onclick={() => openVariantModal(product.id)}
                    class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                  >
                    <Plus size={16} class="mr-1" />
                    Add variant
                  </button>
                </div>
              </div>
            {/if}
          </div>
        {/each}
      {/if}
    </div>
  </div>
</Layout>

<!-- Product Modal -->
<ProductModal
  bind:show={showProductModal}
  product={productToEdit}
  on:close={closeProductModal}
  on:success={handleProductSaved}
/>

<!-- Variant Modal -->
<ProductVariantModal
  bind:show={showVariantModal}
  variant={variantToEdit}
  productId={variantProductId}
  on:close={closeVariantModal}
  on:success={handleVariantSaved}
/>

<!-- Customer Prices Modal -->
<CustomerPricesModal
  bind:show={showCustomerPricesModal}
  variant={customerPricesVariant}
  on:close={closeCustomerPricesModal}
/>

<!-- Delete Confirmation Modal -->
{#if showDeleteModal && deleteTarget}
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" onclick={closeDeleteModal}></div>
    <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 scale-in">
      <div class="p-6">
        <div class="flex items-center space-x-4 mb-4">
          <div class="w-12 h-12 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
            <AlertTriangle size={24} class="text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">
              {deleteTarget.type === 'product' ? $_('products.delete') : $_('products.variants.delete')}
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {$_('confirmModal.cannotUndo')}
            </p>
          </div>
        </div>

        <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
          <p class="text-sm text-gray-700 dark:text-gray-300">
            {#if deleteTarget.type === 'product'}
              {$_('products.deleteConfirm')}
            {:else}
              {$_('products.variants.deleteConfirm')}
            {/if}
          </p>
          <p class="text-sm font-medium text-gray-900 dark:text-white mt-2">
            {deleteTarget.item.code} - {deleteTarget.item.name}
          </p>
        </div>

        <div class="flex justify-end space-x-3">
          <button
            onclick={closeDeleteModal}
            class="btn btn-secondary"
          >
            {$_('common.cancel')}
          </button>
          <button
            onclick={handleConfirmDelete}
            disabled={deletingProduct || deletingVariant}
            class="btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
          >
            {#if deletingProduct || deletingVariant}
              <div class="loading-spinner h-4 w-4 mr-2"></div>
            {/if}
            {$_('common.delete')}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<!-- Product Description Modal -->
{#if showDescriptionModal && descriptionProduct}
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" onclick={closeDescriptionModal}></div>
    <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full mx-4 scale-in">
      <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700 flex items-center justify-between bg-linear-to-r from-primary-50/50 dark:from-primary-900/30 to-transparent rounded-t-2xl">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
            <Info class="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 class="text-lg font-bold text-gray-900 dark:text-white">
              {descriptionProduct.name}
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {descriptionProduct.code}
            </p>
          </div>
        </div>
        <button
          onclick={closeDescriptionModal}
          class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
        >
          <X size={20} />
        </button>
      </div>

      <div class="px-6 py-4 max-h-[60vh] overflow-y-auto">
        <div class="prose prose-sm dark:prose-invert max-w-none">
          {@html sanitizeRichText(descriptionProduct.description)}
        </div>
      </div>

      <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end rounded-b-2xl">
        <button
          onclick={closeDescriptionModal}
          class="btn btn-secondary"
        >
          {$_('common.close')}
        </button>
      </div>
    </div>
  </div>
{/if}
